/**
 * API Client
 *
 * Centralized API client for all backend endpoints.
 *
 * Usage:
 * ```typescript
 * import { api } from '@services/api';
 *
 * // Users
 * const users = await api.users.list();
 * const user = await api.users.get('user_id');
 * await api.users.update('user_id', { display_name: 'John' });
 *
 * // Organizations
 * const orgs = await api.organizations.list();
 * const org = await api.organizations.get('org_id');
 * await api.organizations.addMember('org_id', { user_id: 'user_id' });
 *
 * // Invitations
 * const invitations = await api.invitations.list();
 * await api.invitations.create({ invitee_email: 'test@example.com', invitation_type: 'web' });
 * const validation = await api.invitations.validate({ token: 'abc123' });
 *
 * // Push Notifications
 * await api.push.registerToken({ token: 'fcm_token', platform: 'web' });
 * await api.push.send({ title: 'Hello', body: 'World', user_ids: ['user_id'] });
 * ```
 *
 * Adding New Endpoints:
 * 1. Add types to types.ts
 * 2. Create a new service file (e.g., myService.ts)
 * 3. Export it here
 */

import { usersApi } from './users';
import { organizationsApi } from './organizations';
import { invitationsApi } from './invitations';
import { pushNotificationsApi } from './pushNotifications';
import { schedulerApi } from './scheduler';
import { projectsApi } from './projects';
import { partnersApi } from './partners';
import { broadcastsApi } from './broadcasts';
import { emailsSentApi } from './emailsSent';
import { libraryDocumentsApi } from './libraryDocuments';
import { emailTemplatesApi } from './emailTemplates';
import { projectTemplatesApi } from './projectTemplates';
import { syncOperationsApi } from './syncOperations';
import { planningsApi } from './plannings';
import { planningTemplatesApi } from './planningTemplates';
import { messagerieApi } from './messagerie';
import { dossiersApi } from './dossiers';
import { ordresApi } from './ordres';
import { vehiclesApi } from './vehicles';
import { atelierApi } from './atelier';
import { appointmentsApi } from './appointments';
import { disponibilitesApi } from './disponibilites';
import { bookingsApi } from './bookings';
import { paymentsApi } from './payments';
import { estimateExtractionApi } from './estimateExtraction';
import { proxyAdminApi } from './proxyAdmin';

export { default as apiClient } from './client';
export { usersApi } from './users';
export { organizationsApi } from './organizations';
export { invitationsApi } from './invitations';
export { pushNotificationsApi } from './pushNotifications';
export { schedulerApi } from './scheduler';
export { projectsApi } from './projects';
export { partnersApi } from './partners';
export { broadcastsApi } from './broadcasts';
export { emailsSentApi } from './emailsSent';
export { libraryDocumentsApi } from './libraryDocuments';
export { emailTemplatesApi } from './emailTemplates';
export { projectTemplatesApi } from './projectTemplates';
export { syncOperationsApi } from './syncOperations';
export { planningsApi } from './plannings';
export { planningTemplatesApi } from './planningTemplates';
export { messagerieApi } from './messagerie';
export { dossiersApi } from './dossiers';
export { ordresApi } from './ordres';
export { vehiclesApi } from './vehicles';
export { atelierApi } from './atelier';
export { appointmentsApi } from './appointments';
export { disponibilitesApi } from './disponibilites';
export { bookingsApi } from './bookings';
export { paymentsApi } from './payments';
export { estimateExtractionApi } from './estimateExtraction';
export { proxyAdminApi } from './proxyAdmin';
export { communicationsJournalApi } from './communications';

// Re-export types for convenience
export * from './types';

// Default export for convenience
const api = {
  users: usersApi,
  organizations: organizationsApi,
  invitations: invitationsApi,
  push: pushNotificationsApi,
  scheduler: schedulerApi,
  projects: projectsApi,
  partners: partnersApi,
  broadcasts: broadcastsApi,
  emailsSent: emailsSentApi,
  libraryDocuments: libraryDocumentsApi,
  emailTemplates: emailTemplatesApi,
  projectTemplates: projectTemplatesApi,
  syncOperations: syncOperationsApi,
  plannings: planningsApi,
  planningTemplates: planningTemplatesApi,
  messagerie: messagerieApi,
  dossiers: dossiersApi,
  ordres: ordresApi,
  vehicles: vehiclesApi,
  atelier: atelierApi,
  appointments: appointmentsApi,
  disponibilites: disponibilitesApi,
  bookings: bookingsApi,
  payments: paymentsApi,
  estimateExtraction: estimateExtractionApi,
  proxyAdmin: proxyAdminApi,
};

export { api };
export default api;
